import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'motion/react';
import SEO from '@/components/SEO';
import { BackgroundAnimation } from '@/components/background-animation';
import { ArrowLeft, ArrowRight, Clock, BarChart, Lightbulb, Cpu, ChevronRight, HelpCircle, FileText, Bell, Sparkles, BookOpen } from 'lucide-react';
import { DiscordCTA } from '@/components/DiscordCTA';
import { TUTORIALS } from '@/data/tutorials';

const TutorialDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const tutorial = TUTORIALS.find(t => t.slug === slug);

  if (!tutorial) {
    return <Navigate to="/tutorials" replace />;
  }

  const currentIndex = TUTORIALS.findIndex(t => t.slug === slug);
  const prevTutorial = currentIndex > 0 ? TUTORIALS[currentIndex - 1] : null;
  const nextTutorial = currentIndex < TUTORIALS.length - 1 ? TUTORIALS[currentIndex + 1] : null;

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title={`${tutorial.title} | Creator Tutorials`}
        description={tutorial.description}
        keywords={`${tutorial.category} tutorial, physics simulation guide, creator studio`}
        canonical={`/tutorials/${tutorial.slug}`}
      />
      <BackgroundAnimation />

      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <Link
              to="/tutorials"
              className="inline-flex items-center gap-2 text-[9px] font-black text-zinc-400 hover:text-cyan-600 transition-all group uppercase tracking-[0.2em]"
            >
              <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
              Back to Tutorials
            </Link>
          </motion.div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mb-16"
          >
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="px-4 py-1 rounded-full bg-cyan-50 border border-cyan-100 text-[10px] font-bold uppercase tracking-widest text-cyan-600">
                {tutorial.category}
              </span>
              <div className="flex items-center gap-4 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                <div className="flex items-center gap-1.5">
                  <BarChart className="w-3 h-3 text-cyan-500" />
                  {tutorial.level}
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3 h-3 text-cyan-500" />
                  {tutorial.time}
                </div>
              </div>
            </div>
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold font-display text-zinc-950 tracking-tighter leading-[0.95] mb-6">
              {tutorial.title}
            </h1>
            <p className="text-zinc-500 text-lg max-w-3xl leading-relaxed">
              {tutorial.description}
            </p>
          </motion.div>

          {/* Table of Contents */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-16 p-8 rounded-2xl bg-zinc-50/80 border border-zinc-100"
          >
            <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 mb-4">
              In This Tutorial
            </h2>
            <ol className="space-y-2">
              {tutorial.sections.map((section, idx) => (
                <li key={idx}>
                  <a
                    href={`#section-${idx}`}
                    className="flex items-center gap-3 text-sm text-zinc-600 hover:text-cyan-600 transition-colors py-1 group"
                  >
                    <span className="text-[10px] font-black text-zinc-300 group-hover:text-cyan-500 w-5">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <ChevronRight className="w-3 h-3 text-zinc-300 group-hover:text-cyan-500 transition-colors" />
                    <span className="font-medium">{section.heading}</span>
                  </a>
                </li>
              ))}
            </ol>
          </motion.div>

          {/* Sections */}
          <div className="space-y-16">
            {tutorial.sections.map((section, idx) => (
              <motion.div
                key={idx}
                id={`section-${idx}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + idx * 0.05 }}
                className="scroll-mt-32"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-[10px] font-black text-cyan-500 uppercase tracking-[0.2em]">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <div className="h-px flex-1 bg-zinc-100" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold font-display text-zinc-950 tracking-tight mb-6">
                  {section.heading}
                </h2>
                <p className="text-zinc-600 leading-relaxed text-base mb-6">
                  {section.content}
                </p>

                {section.tips && section.tips.length > 0 && (
                  <div className="p-6 rounded-2xl bg-amber-50/60 border border-amber-100 mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Lightbulb className="w-4 h-4 text-amber-500" />
                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-600">
                        Tips
                      </span>
                    </div>
                    <ul className="space-y-2">
                      {section.tips.map((tip, tipIdx) => (
                        <li
                          key={tipIdx}
                          className="flex items-start gap-2 text-sm text-amber-900/80"
                        >
                          <span className="text-amber-400 mt-1">•</span>
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {section.technicalDetails && section.technicalDetails.length > 0 && (
                  <div className="p-6 rounded-2xl bg-blue-50/60 border border-blue-100">
                    <div className="flex items-center gap-2 mb-3">
                      <Cpu className="w-4 h-4 text-blue-500" />
                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600">
                        Technical Details
                      </span>
                    </div>
                    <ul className="space-y-2">
                      {section.technicalDetails.map((detail, detailIdx) => (
                        <li
                          key={detailIdx}
                          className="flex items-start gap-2 text-sm text-blue-900/80 font-mono"
                        >
                          <span className="text-blue-400 mt-1">→</span>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-20 pt-12 border-t border-zinc-100"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {prevTutorial ? (
                <Link
                  to={`/tutorials/${prevTutorial.slug}`}
                  className="group p-6 rounded-2xl border border-zinc-100 hover:border-cyan-200 hover:bg-cyan-50/30 transition-all"
                >
                  <div className="flex items-center gap-2 text-[9px] font-black text-zinc-400 uppercase tracking-[0.2em] mb-2">
                    <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
                    Previous Tutorial
                  </div>
                  <p className="font-bold text-zinc-800 group-hover:text-cyan-600 transition-colors">
                    {prevTutorial.title}
                  </p>
                </Link>
              ) : (
                <div />
              )}
              {nextTutorial && (
                <Link
                  to={`/tutorials/${nextTutorial.slug}`}
                  className="group p-6 rounded-2xl border border-zinc-100 hover:border-cyan-200 hover:bg-cyan-50/30 transition-all text-right"
                >
                  <div className="flex items-center justify-end gap-2 text-[9px] font-black text-zinc-400 uppercase tracking-[0.2em] mb-2">
                    Next Tutorial
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <p className="font-bold text-zinc-800 group-hover:text-cyan-600 transition-colors">
                    {nextTutorial.title}
                  </p>
                </Link>
              )}
            </div>
          </motion.div>

          {/* Next Steps Resource Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mt-16 pt-12 border-t border-zinc-100 space-y-6"
          >
            <h3 className="text-lg font-bold font-display uppercase tracking-tight text-zinc-950">Next Steps & Related Resources</h3>
            <p className="text-zinc-500 text-sm font-medium font-sans">
              Already finished? Continue learning or start creating with these recommended links:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link to="/plans" className="p-4 rounded-2xl bg-zinc-50/50 border border-zinc-100 hover:border-cyan-600/30 flex items-center justify-between group transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-cyan-50 flex items-center justify-center text-cyan-600 group-hover:bg-cyan-600 group-hover:text-white transition-all">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-zinc-800">Pricing & Studio Access</h4>
                    <p className="text-xs text-muted-foreground">Level up your exports & features</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-cyan-600 group-hover:translate-x-1 transition-all" />
              </Link>

              <Link to="/help" className="p-4 rounded-2xl bg-zinc-50/50 border border-zinc-100 hover:border-cyan-600/30 flex items-center justify-between group transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-cyan-50 flex items-center justify-center text-cyan-600 group-hover:bg-cyan-600 group-hover:text-white transition-all">
                    <HelpCircle className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-zinc-800">Help Center</h4>
                    <p className="text-xs text-muted-foreground">Comprehensive parameter documentation</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-cyan-600 group-hover:translate-x-1 transition-all" />
              </Link>

              <Link to="/blog" className="p-4 rounded-2xl bg-zinc-50/50 border border-zinc-100 hover:border-cyan-600/30 flex items-center justify-between group transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-cyan-50 flex items-center justify-center text-cyan-600 group-hover:bg-cyan-600 group-hover:text-white transition-all">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-zinc-800">Creator Blog</h4>
                    <p className="text-xs text-muted-foreground">Virality triggers and physics art insights</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-cyan-600 group-hover:translate-x-1 transition-all" />
              </Link>

              <Link to="/showcase" className="p-4 rounded-2xl bg-zinc-50/50 border border-zinc-100 hover:border-cyan-600/30 flex items-center justify-between group transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-cyan-50 flex items-center justify-center text-cyan-600 group-hover:bg-cyan-600 group-hover:text-white transition-all">
                    <BookOpen className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-zinc-800">Community Showcase</h4>
                    <p className="text-xs text-muted-foreground">Get inspired by what others build</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-cyan-600 group-hover:translate-x-1 transition-all" />
              </Link>

              <Link to="/updates" className="p-4 rounded-2xl bg-zinc-50/50 border border-zinc-100 hover:border-cyan-600/30 flex items-center justify-between group transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-cyan-50 flex items-center justify-center text-cyan-600 group-hover:bg-cyan-600 group-hover:text-white transition-all">
                    <Bell className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-zinc-800">Product Updates</h4>
                    <p className="text-xs text-muted-foreground">Stay current with new releases</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-cyan-600 group-hover:translate-x-1 transition-all" />
              </Link>

              <Link to="/faq" className="p-4 rounded-2xl bg-zinc-50/50 border border-zinc-100 hover:border-cyan-600/30 flex items-center justify-between group transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-cyan-50 flex items-center justify-center text-cyan-600 group-hover:bg-cyan-600 group-hover:text-white transition-all">
                    <HelpCircle className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-zinc-800">Frequently Asked Questions</h4>
                    <p className="text-xs text-muted-foreground">Quick answers to typical queries</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-cyan-600 group-hover:translate-x-1 transition-all" />
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-12"
          >
            <DiscordCTA variant="banner" />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TutorialDetail;
